import { type RovoMemoryBlurbResponse } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export interface UseMemoryBlurbProps extends AssistanceServiceInjectedParams {
	/**
	 * Whether to enable fetching the memory blurb
	 */
	isEnabled: boolean;
}

export interface UseMemoryBlurbReturn {
	/**
	 * The narrative blurb summarising the user's implicit identity,
	 * or null when there is insufficient signal to generate one.
	 */
	blurb: string | null;
	/**
	 * Whether the blurb is currently being fetched
	 */
	isLoading: boolean;
	/**
	 * Any error that occurred while fetching the blurb
	 */
	error: ApiError | null;
	/**
	 * Function to retry fetching the blurb
	 */
	onRetry: () => Promise<void>;
}

/**
 * Hook for fetching the memory blurb — a human-readable narrative summary of
 * the user's implicit identity (role, OKRs, projects, tools) built from TWG
 * activity and conversational memory.
 *
 * Returns `blurb: null` when the backend cannot generate one due to insufficient
 * signal density.
 */
export const useMemoryBlurb: (
	props: Omit<UseMemoryBlurbProps, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam,
) => UseMemoryBlurbReturn = createHookWithAssistanceService(
	({ assistanceServiceInjectedParams, isEnabled }: UseMemoryBlurbProps): UseMemoryBlurbReturn => {
		const { assistanceService } = assistanceServiceInjectedParams;

		const {
			data: blurbResponse,
			isFetching,
			refetch,
			error,
		} = useQuery<RovoMemoryBlurbResponse>({
			queryKey: ['rovo-memory-blurb'],
			enabled: isEnabled,
			queryFn: assistanceService.getRovoMemoryBlurb,
		});

		const handleRetry = async () => {
			await refetch();
		};

		return {
			blurb: blurbResponse?.content ?? null,
			isLoading: isFetching,
			error,
			onRetry: handleRetry,
		};
	},
);
